import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaCheckCircle, FaFileImage } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import mqtt from 'mqtt';

interface VideoStreamProps {
  testConditions: any; // Define the expected type for testConditions
}

// Custom type for video constraints with advanced options
interface CustomMediaTrackConstraintSet extends MediaTrackConstraintSet {
  focusMode?: string;
  exposureMode?: string;
  whiteBalanceMode?: string;
  zoom?: number;
}

interface VideoTrackConstraints extends MediaTrackConstraints {
  advanced?: CustomMediaTrackConstraintSet[];
}

const MQTT_BROKER_URL = 'wss://dev-emqxlistener.ops.enncrypto.com/mqtt';
const MQTT_TOPIC = 'test-vyu-app-datacapture/+/status';

const VideoStream: React.FC<VideoStreamProps> = ({ testConditions }) => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const bifrostId = urlParams.get('id');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const [time, setTime] = useState(0); // Time in seconds
  const [isRunning, setIsRunning] = useState(false); // Timer running state
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showSessionEnd, setSessionEnd] = useState(false);
  const [showMessage, setShowMessage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const clientRef = useRef<any>(null);
  const [imageCount, setImageCount] = useState(0);
  const lastBytesSentRef = useRef<number>(0);
  const lastTimestampRef = useRef<number>(0);
  const bandwidthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [cornerOverlayVisible, setCornerOverlayVisible] = useState<boolean>(true);
  
  // New state for retry logic
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Progress indicator states
  const [mqttStatus, setMqttStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [webrtcStatus, setWebrtcStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [signalStatus, setSignalStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  
  // State for tracking image count timeout
  const imageCountTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const startRTCConnection = async (stream: MediaStream, isRetry: boolean = false) => {
    console.log(`${isRetry ? 'Retrying' : 'Starting'} RTC connection...`);
    
    // Reset WebRTC status when starting/retrying
    setWebrtcStatus('pending');
    
    if (isRetry) {
      setShowMessage(`Retrying connection (${retryCount + 1}/${maxRetries})`);
    }
    
    // Create a new RTCPeerConnection instance with optimized configuration
    peerConnectionRef.current = new RTCPeerConnection({
      iceServers: [
        {
          urls: 'turn:3.227.0.206:3478',
          username: 'admin',
          credential: 'webkul123',
        },
        {
          urls: 'turn:3.239.83.210:3478',
          username: 'admin',
          credential: 'webkul123'
        },
        // Add STUN servers for better connectivity
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ],
      iceTransportPolicy: 'all',
      iceCandidatePoolSize: 10, // Pre-gather ICE candidates
      bundlePolicy: 'max-bundle', // Bundle all media on single transport
      rtcpMuxPolicy: 'require' // Multiplex RTP and RTCP
    });
  
    console.log('Peer connection created:', peerConnectionRef.current);
  
    // Get video tracks and apply constraints
    const videoTracks = stream.getVideoTracks();
    const videoTrack = videoTracks.length > 0 ? videoTracks[0] : null;
    
    if (videoTrack) {
      const capabilities = videoTrack.getCapabilities();
      console.log('Video capabilities:', capabilities);
      
      const resWidth = 2160;
      const resHeight = 1440;
      const maxWidth = capabilities.width?.max ?? resWidth;
      const maxHeight = capabilities.height?.max ?? resHeight;
      const width = Math.min(maxWidth, resWidth);
      const height = Math.min(maxHeight, resHeight);
      const frameRate = 6;
      
      console.log(`Using resolution: ${width}x${height} @ ${frameRate}fps`);
      
      try {
        await videoTrack.applyConstraints({
          width: { ideal: width, min: Math.floor(width * 0.9) },
          height: { ideal: height, min: Math.floor(height * 0.9) },
          frameRate: { ideal: frameRate, min: 1 }
        });
        console.log('Video constraints applied for high quality');
      } catch (err) {
        console.warn('Failed to apply high quality video constraints:', err);
        try {
          await videoTrack.applyConstraints({
            width: { ideal: width, min: Math.floor(width * 0.8) },
            height: { ideal: height, min: Math.floor(height * 0.8) },
            frameRate: { ideal: frameRate, min: 1 }
          });
          console.log('Applied fallback constraints with minimum resolution guarantees');
        } catch (fallbackErr) {
          console.error('Failed to apply even fallback constraints:', fallbackErr);
        }
      }
    }

    // Add tracks to peer connection
    stream.getTracks().forEach((track) => {
      console.log(`Adding track: ${track.kind}`);
      if (track.kind === 'video') {
        track.applyConstraints({
          advanced: [],
        });
      }
      peerConnectionRef.current?.addTrack(track, stream);
    });
  
    console.log('Tracks added to peer connection.');
  
    try {
      // Configure RTC peer connection for high quality
      if (peerConnectionRef.current.getConfiguration) {
        const currentConfig = peerConnectionRef.current.getConfiguration();
        const enhancedConfig = {
          ...currentConfig,
          sdpSemantics: 'unified-plan',
          enableDscp: true,
          offerExtmapAllowMixed: true
        };
        
        try {
          peerConnectionRef.current.setConfiguration(enhancedConfig);
          console.log('Enhanced RTCPeerConnection configuration applied');
        } catch (err) {
          console.warn('Failed to set enhanced configuration:', err);
        }
      }
      
      // Create offer with quality-focused options
      const offerOptions = {
        offerToReceiveVideo: true,
        offerToReceiveAudio: false,
        iceRestart: true,
        voiceActivityDetection: false
      };
      
      const offer: RTCSessionDescriptionInit = await peerConnectionRef.current.createOffer(offerOptions);

      // Enhance SDP for higher quality
      let enhancedSdp = setMaxBitrate(offer.sdp, 24000000); // 24 Mbps

      // Add custom attributes for better quality
      if (enhancedSdp.includes('m=video')) {
        enhancedSdp = enhancedSdp.replace(/m=video.*\r\n/, (match) => {
          return match + 'a=x-google-flag:cpu-overuse-detection=false\r\n';
        });
      }

      // Add VP9 high bitrate settings
      enhancedSdp = enhancedSdp.replace(/a=rtpmap:(\d+) VP9\/\d+\r\n/g, (match, p1) => {
        return `${match}a=fmtp:${p1} x-google-max-bitrate=24000;x-google-min-bitrate=12000;profile-id=2;tias=24000000\r\n` +
               `a=rtcp-fb:${p1} nack\r\n` +
               `a=rtcp-fb:${p1} nack pli\r\n` +
               `a=rtcp-fb:${p1} ccm fir\r\n` +
               `a=rtcp-fb:${p1} transport-cc\r\n`;
      });

      // Add H264 high bitrate settings
      enhancedSdp = enhancedSdp.replace(/a=rtpmap:(\d+) H264\/\d+\r\n/g, (match, p1) => {
        return `${match}a=fmtp:${p1} level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f;x-google-max-bitrate=24000;x-google-min-bitrate=12000\r\n` +
               `a=rtcp-fb:${p1} nack\r\n` +
               `a=rtcp-fb:${p1} nack pli\r\n` +
               `a=rtcp-fb:${p1} ccm fir\r\n` +
               `a=rtcp-fb:${p1} transport-cc\r\n`;
      });

      enhancedSdp = disableResolutionDownscaling(enhancedSdp);
      enhancedSdp = addFEC(enhancedSdp);
      offer.sdp = enhancedSdp;

      console.log('Enhanced offer created:', offer);
      await peerConnectionRef.current.setLocalDescription(new RTCSessionDescription(offer));
      console.log('Local description set. Signaling state:', peerConnectionRef.current.signalingState);
    } catch (error) {
      console.error('Error creating offer or setting local description:', error);
      setWebrtcStatus('failed');
    }
  
    // FIXED: ICE gathering timeout set to 5 seconds (5000ms)
    const iceGatheringTimeout = 5000; // 5 seconds maximum
    const iceGatheringPromise = new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.warn('ICE gathering timeout reached (5 seconds)');
        reject(new Error('ICE gathering timeout'));
      }, iceGatheringTimeout);
  
      function checkState() {
        console.log('ICE gathering state change:', peerConnectionRef?.current?.iceGatheringState);
        if (peerConnectionRef?.current?.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          peerConnectionRef.current?.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }
      }
  
      if (peerConnectionRef?.current?.iceGatheringState === 'complete') {
        clearTimeout(timeout);
        resolve();
      } else {
        peerConnectionRef?.current?.addEventListener('icegatheringstatechange', checkState);
      }
    });
  
    try {
      await iceGatheringPromise;
      console.log('ICE gathering complete');
    } catch (error) {
      console.error('ICE gathering failed or timed out:', error);
      // Continue anyway - partial ICE gathering may still work
    }
  
    // Set up connection state listeners with enhanced recovery logic
    setupConnectionStateMonitoring(peerConnectionRef.current, stream);
  
    peerConnectionRef.current?.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        console.log('New ICE candidate:', event.candidate);
        // Log candidate type for debugging
        console.log('Candidate type:', event.candidate.type, 'Protocol:', event.candidate.protocol);
      } else {
        console.log('All ICE candidates have been gathered.');
      }
    });
  
    peerConnectionRef.current?.addEventListener('track', (event) => {
      console.log('Track event received:', event);
    });
  
    // Reduced delay for faster connection - send signal immediately after local description is set
    setTimeout(() => {
      if (peerConnectionRef.current?.localDescription) {
        console.log('Sending offer to signaling server...');
        sendBifrostSignal(peerConnectionRef.current, peerConnectionRef.current.localDescription);
        console.log('Signal sent:', peerConnectionRef.current.localDescription);
      } else {
        console.error('Local description not available.');
        setSignalStatus('failed');
      }
    }, 100); // Reduced from 200ms to 100ms
  };

  // New function to handle connection failures and retries
  const handleConnectionFailure = (stream: MediaStream) => {
    if (retryCount < maxRetries) {
      console.log(`Connection failed, retrying... (${retryCount + 1}/${maxRetries})`);
      setRetryCount(prev => prev + 1);
      
      // Clean up current connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      
      // Clear any existing retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      // Retry after a delay (exponential backoff)
      const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 8000); // Max 8 seconds
      retryTimeoutRef.current = setTimeout(() => {
        startRTCConnection(stream, true);
      }, retryDelay);
      
    } else {
      console.error('Max retries reached, connection failed permanently');
      setShowMessage('Connection failed. Please try again.');
      setRetryCount(0);
      
      // Optionally stop the stream after max retries
      setTimeout(() => {
        stopVideoStream();
      }, 3000);
    }
  };

  // Enhanced connection recovery with ICE restart
  const attemptConnectionRecovery = async (): Promise<void> => {
    console.log('Attempting connection recovery...');

    if (!peerConnectionRef.current) return;

    // First try an ICE restart
    await attemptICERestart();

    // If still failing after 3 seconds, try a full reconnection
    setTimeout(() => {
      if (peerConnectionRef.current?.connectionState === 'failed') {
        console.log('ICE restart unsuccessful, attempting full reconnection');
        restartFullConnection();
      }
    }, 3000);
  };

  // ICE restart mechanism
  const attemptICERestart = async (): Promise<void> => {
    console.log('Attempting ICE restart...');

    if (!peerConnectionRef.current) return;

    try {
      const offer = await peerConnectionRef.current.createOffer({
        iceRestart: true,
        offerToReceiveVideo: true,
        offerToReceiveAudio: false
      });

      // Apply the same SDP enhancements
      let enhancedSdp = setMaxBitrate(offer.sdp, 24000000);
      
      // Add custom attributes for better quality
      if (enhancedSdp.includes('m=video')) {
        enhancedSdp = enhancedSdp.replace(/m=video.*\r\n/, (match) => {
          return match + 'a=x-google-flag:cpu-overuse-detection=false\r\n';
        });
      }

      // Add VP9 high bitrate settings
      enhancedSdp = enhancedSdp.replace(/a=rtpmap:(\d+) VP9\/\d+\r\n/g, (match, p1) => {
        return `${match}a=fmtp:${p1} x-google-max-bitrate=24000;x-google-min-bitrate=12000;profile-id=2;tias=24000000\r\n` +
               `a=rtcp-fb:${p1} nack\r\n` +
               `a=rtcp-fb:${p1} nack pli\r\n` +
               `a=rtcp-fb:${p1} ccm fir\r\n` +
               `a=rtcp-fb:${p1} transport-cc\r\n`;
      });

      // Add H264 high bitrate settings
      enhancedSdp = enhancedSdp.replace(/a=rtpmap:(\d+) H264\/\d+\r\n/g, (match, p1) => {
        return `${match}a=fmtp:${p1} level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f;x-google-max-bitrate=24000;x-google-min-bitrate=12000\r\n` +
               `a=rtcp-fb:${p1} nack\r\n` +
               `a=rtcp-fb:${p1} nack pli\r\n` +
               `a=rtcp-fb:${p1} ccm fir\r\n` +
               `a=rtcp-fb:${p1} transport-cc\r\n`;
      });

      enhancedSdp = disableResolutionDownscaling(enhancedSdp);
      enhancedSdp = addFEC(enhancedSdp);
      
      const offerDesc = new RTCSessionDescription({ type: 'offer', sdp: enhancedSdp });
      await peerConnectionRef.current.setLocalDescription(offerDesc);

      // Wait for ICE gathering with new candidates
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn('ICE restart gathering timeout');
          resolve(); // Don't fail completely on timeout
        }, 5000);

        const checkState = () => {
          if (peerConnectionRef.current?.iceGatheringState === 'complete') {
            clearTimeout(timeout);
            peerConnectionRef.current.removeEventListener('icegatheringstatechange', checkState);
            resolve();
          }
        };

        if (peerConnectionRef.current?.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        } else {
          peerConnectionRef.current?.addEventListener('icegatheringstatechange', checkState);
        }
      });

      if (peerConnectionRef.current?.localDescription) {
        sendBifrostSignal(peerConnectionRef.current, peerConnectionRef.current.localDescription);
      }
    } catch (error) {
      console.error('ICE restart failed:', error);
      // Don't throw - let the caller handle next steps
    }
  };

  // Full connection restart mechanism
  const restartFullConnection = (): void => {
    console.log('Performing full connection restart...');

    // Clean up existing connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear any existing timeouts
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Reset retry count
    setRetryCount(0);
    
    // Generate new session ID for complete restart
    const oldSessionId = sessionId;
    const newSessionId = typeof crypto.randomUUID === 'function' 
      ? crypto.randomUUID() 
      : generateUUID();
    
    setSessionId(newSessionId);
    
    console.log(`Full connection restart: ${oldSessionId} -> ${newSessionId}`);

    // Start a new connection after a brief delay
    setTimeout(() => {
      if (streamRef.current) {
        // Reset status indicators
        setWebrtcStatus('pending');
        setSignalStatus('pending');
        
        // Restart RTC connection with existing stream
        startRTCConnection(streamRef.current, false);
      } else {
        // If no stream, restart everything
        startVideoStream();
      }
    }, 1000);
  };

  // Enhanced connection state monitoring
  const setupConnectionStateMonitoring = (peerConnection: RTCPeerConnection, stream: MediaStream) => {
    // ICE connection state monitoring with recovery
    peerConnection.addEventListener('iceconnectionstatechange', () => {
      const iceState = peerConnection.iceConnectionState;
      console.log('ICE connection state change:', iceState);
      
      if (iceState === 'connected' || iceState === 'completed') {
        console.log('ICE connection successful');
        setWebrtcStatus('success');
        setShowMessage('Connected successfully');
        setRetryCount(0); // Reset retry count on success
        
        // Clear success message after 2 seconds
        setTimeout(() => {
          setShowMessage('');
        }, 2000);
      } else if (iceState === 'failed') {
        console.error('ICE connection failed.');
        setWebrtcStatus('failed');
        handleConnectionFailure(stream);
      } else if (iceState === 'disconnected') {
        console.warn('ICE connection disconnected');
        setShowMessage('Connection lost, attempting to reconnect...');
        // Give it some time to reconnect naturally before taking action
        setTimeout(() => {
          if (peerConnection.iceConnectionState === 'disconnected') {
            attemptConnectionRecovery();
          }
        }, 2000);
      } else if (iceState === 'checking') {
        setShowMessage('Reconnecting...');
      }
    });

    // General connection state monitoring
    peerConnection.addEventListener('connectionstatechange', () => {
      const connectionState = peerConnection.connectionState;
      console.log('Connection state change:', connectionState);
      
      if (connectionState === 'connected') {
        setShowMessage('Connection established');
        setTimeout(() => {
          setShowMessage('');
        }, 2000);
      } else if (connectionState === 'failed') {
        console.error('Connection failed.');
        setWebrtcStatus('failed');
        attemptConnectionRecovery();
      } else if (connectionState === 'disconnected') {
        console.warn('Connection disconnected');
        setShowMessage('Connection interrupted...');
      } else if (connectionState === 'connecting') {
        setShowMessage('Connecting...');
      }
    });

    // ICE candidate error monitoring
    peerConnection.addEventListener('icecandidateerror', (event: any) => {
      console.warn('ICE candidate error:', {
        address: event.address || 'unknown',
        port: event.port || 'unknown',
        url: event.url || 'unknown',
        errorCode: event.errorCode || 'unknown',
        errorText: event.errorText || 'unknown'
      });
      
      // Don't immediately fail on candidate errors, but log them
      if (event.errorCode && event.errorCode >= 400 && event.errorCode < 500) {
        console.warn('ICE candidate authentication/authorization error');
      }
    });

    // Data channel error monitoring (if applicable)
    peerConnection.addEventListener('datachannel', (event) => {
      const channel = event.channel;
      channel.addEventListener('error', (error) => {
        console.error('Data channel error:', error);
      });
    });
  };
  
  // Helper function to add Forward Error Correction (FEC)
  const addFEC = (sdp: string): string => {
    const lines = sdp.split('\r\n');
    let result = [];
    let isVideoSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      result.push(line);
      
      if (line.startsWith('m=video')) {
        isVideoSection = true;
      }
      
      if (isVideoSection && line.startsWith('a=rtpmap:') && 
         (line.includes('VP8') || line.includes('VP9') || line.includes('H264'))) {
        const codecId = line.split(':')[1].split(' ')[0];
        result.push(`a=rtcp-fb:${codecId} nack`);
        result.push(`a=rtcp-fb:${codecId} nack pli`);
        result.push(`a=rtcp-fb:${codecId} ccm fir`);
        result.push(`a=rtcp-fb:${codecId} transport-cc`);
      }
    }
    
    return result.join('\r\n');
  };
  
  // Helper function to disable resolution downscaling
  const disableResolutionDownscaling = (sdp: string): string => {
    let modifiedSdp = sdp.replace(/a=fmtp:.*scaleResolutionDownBy=[1-9][.0-9]*/g, (match) => {
      return match.replace(/scaleResolutionDownBy=[1-9][.0-9]*/, 'scaleResolutionDownBy=1.0');
    });
    
    if (!modifiedSdp.includes('a=rid:')) {
      const videoMediaIndex = modifiedSdp.indexOf('m=video');
      if (videoMediaIndex !== -1) {
        const videoSection = modifiedSdp.substring(videoMediaIndex);
        const endOfVideoSection = videoSection.indexOf('m=', 2);
        const videoSectionEnd = endOfVideoSection !== -1 ? videoMediaIndex + endOfVideoSection : modifiedSdp.length;
        
        if (!videoSection.includes('a=msid:')) {
          const insertPosition = videoSectionEnd;
          modifiedSdp = modifiedSdp.substring(0, insertPosition) + 
                        'a=msid:highres highres-video\r\n' + 
                        modifiedSdp.substring(insertPosition);
        }
      }
    }
    
    return modifiedSdp;
  };
  
  // Start monitoring bandwidth and video quality
  const startBandwidthMonitoring = (stream: MediaStream) => {
    if (bandwidthCheckIntervalRef.current) {
      clearInterval(bandwidthCheckIntervalRef.current);
    }
    
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack || !peerConnectionRef.current) return;
    
    const videoSender = peerConnectionRef.current.getSenders()
      .find(sender => sender.track?.kind === 'video');
    
    if (!videoSender) return;
    
    try {
      const parameters = videoSender.getParameters();
      parameters.degradationPreference = 'maintain-resolution';
      
      if (!parameters.encodings) {
        parameters.encodings = [{}];
      }
      
      parameters.encodings.forEach(encoding => {
        encoding.scaleResolutionDownBy = 1.0;
        encoding.maxBitrate = 24000000; // 24 Mbps
        (encoding as any).priority = 'high';
      });
      
      videoSender.setParameters(parameters).catch(err => {
        console.warn('Failed to set encoding parameters:', err);
      });
    } catch (err) {
      console.warn('Error configuring video sender parameters:', err);
    }
    
    bandwidthCheckIntervalRef.current = setInterval(async () => {
      try {
        if (!peerConnectionRef.current || !videoSender) return;
        
        const stats = await peerConnectionRef.current.getStats(videoSender.track || null);
        let currentBitrate = 0;
        let currentWidth = 0;
        let currentHeight = 0;
        let currentFrameRate = 0;
        
        stats.forEach(report => {
          if (report.type === 'outbound-rtp' && report.kind === 'video') {
            const bytesSent = report.bytesSent;
            const timestamp = report.timestamp;
            
            if (lastBytesSentRef.current && lastTimestampRef.current) {
              currentBitrate = 8 * (bytesSent - lastBytesSentRef.current) / 
                             (timestamp - lastTimestampRef.current) * 1000;
            }
            
            lastBytesSentRef.current = bytesSent;
            lastTimestampRef.current = timestamp;
            
            if (report.frameWidth) currentWidth = report.frameWidth;
            if (report.frameHeight) currentHeight = report.frameHeight;
            if (report.framesPerSecond) currentFrameRate = report.framesPerSecond;
          }
        });
        
        console.log(`Current stats: ${currentWidth}x${currentHeight} @ ${currentFrameRate}fps, bitrate: ${Math.round(currentBitrate/1000)}kbps`);
        
        const targetWidth = 2160;
        const targetHeight = 1440;
        const resolutionDropped = currentWidth < targetWidth * 0.9 || currentHeight < targetHeight * 0.9;
        const lowBitrate = currentBitrate < 10000000; // 10 Mbps threshold
        
        if (resolutionDropped || lowBitrate) {
          console.log('Low bandwidth or resolution drop detected, adjusting parameters...');
          
          if (videoTrack) {
            try {
              await videoTrack.applyConstraints({
                width: { ideal: targetWidth, min: Math.floor(targetWidth * 0.9) },
                height: { ideal: targetHeight, min: Math.floor(targetHeight * 0.9) },
                frameRate: { ideal: Math.max(currentFrameRate - 1, 1), min: 1 }
              });
              
              const parameters = videoSender.getParameters();
              parameters.degradationPreference = 'maintain-resolution';
              
              if (parameters.encodings) {
                parameters.encodings.forEach(encoding => {
                  encoding.scaleResolutionDownBy = 1.0;
                });
              }
              
              await videoSender.setParameters(parameters);
              console.log('Adjusted parameters to maintain resolution quality');
            } catch (err) {
              console.warn('Failed to adjust video parameters:', err);
            }
          }
        }
      } catch (error) {
        console.error('Error in bandwidth monitoring:', error);
      }
    }, 2000);
  };
  
  useEffect(() => {
    const sessionId = typeof crypto.randomUUID === 'function' 
      ? crypto.randomUUID() 
      : generateUUID();
  
    setSessionId(sessionId);

    return () => {
      // Cleanup retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      
      // Cleanup image count timeout
      if (imageCountTimeoutRef.current) {
        clearTimeout(imageCountTimeoutRef.current);
      }
      
      if (clientRef.current) {
        clientRef.current.unsubscribe(MQTT_TOPIC, () => {
          console.log(`Unsubscribed from ${MQTT_TOPIC}`);
        });
        clientRef.current.end(() => {
          console.log('MQTT connection closed');
        });
      }
      if (bandwidthCheckIntervalRef.current) {
        clearInterval(bandwidthCheckIntervalRef.current);
      }
    };
  }, []);

  // Function to establish MQTT connection
  const connectMQTT = () => {
    if (clientRef.current) {
      return; // Already connected or connecting
    }

    setMqttStatus('pending');
    
    clientRef.current = mqtt.connect(MQTT_BROKER_URL, {
      username: 'admin',
      password: '8hEg1F3q3KuP1qy',
      clean: true,
      clientId: sessionId,
      rejectUnauthorized: false,
    });
  
    if (clientRef.current) {
      clientRef.current.on('connect', () => {
        console.log('MQTT connected successfully');
        setMqttStatus('success');
        clientRef.current.subscribe(MQTT_TOPIC);
      });

      clientRef.current.on('error', (error: any) => {
        console.error('MQTT connection error:', error);
        setMqttStatus('failed');
      });

      clientRef.current.on('offline', () => {
        console.warn('MQTT connection offline');
        setMqttStatus('failed');
      });
  
      clientRef.current.on('message', (topic: string, message: Buffer) => {
        const obj = JSON.parse(message.toString());
        if (
          obj &&
          obj.sessionId === sessionId &&
          obj.status === 'image capture'
        ) {
          setImageCount((prevImageCount) => prevImageCount + 1);
        }
      });
    }
  };

  // Function to disconnect MQTT
  const disconnectMQTT = () => {
    if (clientRef.current) {
      clientRef.current.unsubscribe(MQTT_TOPIC, () => {
        console.log(`Unsubscribed from ${MQTT_TOPIC}`);
      });
      clientRef.current.end(() => {
        console.log('MQTT connection closed');
      });
      clientRef.current = null;
    }
    setMqttStatus('pending');
  };
  
  function generateUUID() {
    const buffer = new Uint32Array(4);
    crypto.getRandomValues(buffer);
    return Array.from(buffer)
      .map(val => val.toString(16).padStart(8, '0'))
      .join('');
  }
  
  // Check if all services are successful and start timer
  useEffect(() => {
    if (mqttStatus === 'success' && webrtcStatus === 'success' && signalStatus === 'success' && !isRunning) {
      console.log('All services connected successfully, starting timer...');
      startTimer();
    }
  }, [mqttStatus, webrtcStatus, signalStatus, isRunning]);

  // Check for image count timeout after all connections are successful
  useEffect(() => {
    // Only proceed if all connections are successful
    if (mqttStatus === 'success' && webrtcStatus === 'success' && signalStatus === 'success') {
      // Clear any existing timeout
      if (imageCountTimeoutRef.current) {
        clearTimeout(imageCountTimeoutRef.current);
      }
      
      // Set a 5-second timeout to show the awaiting images message
      imageCountTimeoutRef.current = setTimeout(() => {
        if (mqttStatus === 'success' && webrtcStatus === 'success' && signalStatus === 'success') {
          setShowMessage('All connections successful, awaiting images...');
        }
      }, 5000);
    }

    return () => {
      if (imageCountTimeoutRef.current) {
        clearTimeout(imageCountTimeoutRef.current);
      }
    };
  }, [imageCount, mqttStatus, webrtcStatus, signalStatus]); // Added imageCount as dependency
  
  const startTimer = () => {
    if (!isRunning) {
      setIsRunning(true);
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
    }
  };

  const stopTimer = () => {
    setIsRunning(false);
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const formatTime = (seconds: any) => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  const createCornerOverlay = () => {
    if (!cornerOverlayVisible) return null;
    
    const cornerStyle = {
      position: 'absolute' as 'absolute',
      width: '40px',
      height: '40px',
      border: '4px solid white',
      zIndex: 10,
    };
    
    const topLeftStyle = {
      ...cornerStyle,
      top: 0,
      left: 0,
      borderRight: 'none',
      borderBottom: 'none',
    };
    
    const topRightStyle = {
      ...cornerStyle,
      top: 0,
      right: 0,
      borderLeft: 'none',
      borderBottom: 'none',
    };
    
    const bottomLeftStyle = {
      ...cornerStyle,
      bottom: 0,
      left: 0,
      borderRight: 'none',
      borderTop: 'none',
    };
    
    const bottomRightStyle = {
      ...cornerStyle,
      bottom: 0,
      right: 0,
      borderLeft: 'none',
      borderTop: 'none',
    };
    
    const overlayContainerStyle = {
      position: 'absolute' as 'absolute',
      top: '50%',
      left: '50%',
      width: '90%',
      height: '360px',
      transform: 'translate(-50%, -50%)',
      zIndex: 10,
      pointerEvents: 'none' as 'none',
    };
    
    return (
      <div style={overlayContainerStyle}>
        <div style={topLeftStyle}></div>
        <div style={topRightStyle}></div>
        <div style={bottomLeftStyle}></div>
        <div style={bottomRightStyle}></div>
      </div>
    );
  };

  const removeCornerOverlay = () => {
    setCornerOverlayVisible(false);
  };

  const sendBifrostSignal = async (
    peerConnection: RTCPeerConnection,
    offer: RTCSessionDescription
  ) => {
    try {
      console.log('Inside sendBifrostSignal', testConditions);
      
      // Reset signal status when starting
      setSignalStatus('pending');
  
      console.log(
        'Current signaling state before setting remote description:',
        peerConnection.signalingState
      );
      
      const date = new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
      const randomNumber = Math.floor(Math.random() * 900) + 100;
      const formattedTimestamp = `${day}${month}${year}${hours}${minutes}${seconds}${milliseconds}${randomNumber}`;
  
      console.log(formattedTimestamp);
  
      const url = bifrostId
        ? `https://bifrost${Number(bifrostId)}-dev.ops.enncrypto.com/signal`
        : `https://d.api.enncrypto.com/signal`;
      
      let formattedLabel = testConditions?.test_case?.toLowerCase();
      const tempFL = formattedLabel;
      const additionalInfo = { ...testConditions };
      delete additionalInfo['product_scan_id'];
      delete additionalInfo['decoderConfigId'];
      delete additionalInfo['image_tag'];
      delete additionalInfo['test_case'];
      delete additionalInfo['imageTag'];
      
      formattedLabel = [
        'copy prints',
        'scan prints',
        'photocopy',
        'scan 300dpi',
        'scan 600dpi',
        'scan 1200dpi',
      ].includes(formattedLabel)
        ? 'copies'
        : formattedLabel;
      
      let label = {};
      if (formattedLabel === 'copies') {
        label = {
          taskId: '',
          label: formattedLabel,
          category: tempFL,
        };
      } else {
        label = {
          taskId: '',
          label: formattedLabel,
        };
      }
  
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionDescription: offer,
          sessionId,
          context: {
            imageTag: testConditions.image_tag,
            decoderConfigId: testConditions.decoderConfigId,
            scanId: testConditions.product_scan_id,
            imageSource: 'test-vyu-app-stream',
            test_case: testConditions.test_case,
            projectId: testConditions.project,
            classLabel: [label],
            batchId: formattedTimestamp,
            additional_info: {
              ...additionalInfo,
            },
            source: 'stream',
          },
        }),
      });

      // Check if the response is successful
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Signal failed - HTTP ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.message) {
            errorMessage += `: ${errorJson.message}`;
          } else if (errorJson.error) {
            errorMessage += `: ${errorJson.error}`;
          }
        } catch (parseError) {
          if (errorText) {
            errorMessage += `: ${errorText.substring(0, 50)}`;
          }
        }
        
        console.error('Signal API failed:', errorMessage);
        setSignalStatus('failed');
        setShowMessage(errorMessage);
        
        // Attempt recovery on signal failure
        if (response.status >= 500) {
          // Server error - retry after delay
          setTimeout(() => {
            if (peerConnectionRef.current?.localDescription) {
              sendBifrostSignal(peerConnectionRef.current, peerConnectionRef.current.localDescription);
            }
          }, 2000);
        }
        return;
      }
  
      const answer = await response.json();
      console.log('Received answer from server:', answer);

      // Check if the answer contains error information
      if (answer.error) {
        const errorMessage = `Signal failed - ${answer.error}`;
        console.error('Signal API returned error:', errorMessage);
        setSignalStatus('failed');
        setShowMessage(errorMessage);
        return;
      }

      // Validate that we received a proper session description
      if (!answer.type || !answer.sdp) {
        const errorMessage = 'Signal failed - Invalid response format';
        console.error('Invalid session description received:', answer);
        setSignalStatus('failed');
        setShowMessage(errorMessage);
        return;
      }
  
            
      console.log(
        'Signaling state after receiving answer:',
        peerConnection.signalingState
      );

      // Enhanced signaling state handling for better compatibility
      const isiOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const isiOSPro = isiOS && /iPhone1[345]/.test(navigator.userAgent);

      console.log(`Current signaling state: ${peerConnection.signalingState}, iOS: ${isiOS}, iOS Pro: ${isiOSPro}`);
  
      if (
        peerConnection.signalingState === 'have-local-offer' ||
        peerConnection.signalingState === 'have-remote-offer'
      ) {
        console.log('Setting remote description...');
        try {
          await peerConnection.setRemoteDescription(
            new RTCSessionDescription(answer)
          );
        } catch (setRemoteError) {
          const errorMessage = `Signal failed - Remote description error: ${setRemoteError instanceof Error ? setRemoteError.message : 'Unknown error'}`;
          console.error('Failed to set remote description:', setRemoteError);
          setSignalStatus('failed');
          setShowMessage(errorMessage);
          
          // Attempt recovery on remote description failure
          if (setRemoteError instanceof Error && setRemoteError.name === 'InvalidStateError') {
            console.log('Attempting to recover from InvalidStateError...');
            setTimeout(() => {
              attemptICERestart();
            }, 1000);
          }
          return;
        }
      } else if (peerConnection.signalingState === 'stable' && isiOSPro) {
        // Special handling for iOS Pro devices in stable state
        console.log('iOS Pro device detected in stable state - creating new offer before setting remote');

        try {
          // Create a new offer to transition from stable state
          const newOffer = await peerConnection.createOffer({
            offerToReceiveVideo: true,
            offerToReceiveAudio: false,
          });

          // Set local description to transition from stable to have-local-offer
          await peerConnection.setLocalDescription(newOffer);

          // Wait for state transition
          await new Promise(resolve => {
            const checkState = () => {
              const currentState = peerConnection.signalingState;
              if (currentState === 'have-local-offer') {
                peerConnection.removeEventListener('signalingstatechange', checkState);
                resolve(null);
              }
            };
            peerConnection.addEventListener('signalingstatechange', checkState);
            // Safety timeout
            setTimeout(resolve, 1000);
          });

          // Now set the remote description (use type assertion to override TS inference)
          const currentSignalingState = peerConnection.signalingState as RTCSignalingState;
          console.log(`Current state after transition: ${currentSignalingState}`);
          
          if (currentSignalingState === 'have-local-offer' || currentSignalingState === 'have-remote-offer') {
            console.log('Now in correct state, setting remote description');
            await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
          } else {
            console.warn(`Still in incorrect state: ${currentSignalingState}, attempting to set remote anyway`);
            try {
              await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
            } catch (e) {
              console.error('Final attempt to set remote description failed:', e);
              attemptConnectionRecovery();
              return;
            }
          }
        } catch (iosError) {
          console.error('iOS Pro recovery failed:', iosError);
          setSignalStatus('failed');
          setShowMessage('iOS compatibility error');
          attemptConnectionRecovery();
          return;
        }
      } else {
        const errorMessage = `Signal failed - Invalid peer connection state: ${peerConnection.signalingState}`;
        console.error('Peer connection is not in a state to set remote description.');
        setSignalStatus('failed');
        setShowMessage(errorMessage);
        
        // Attempt recovery for invalid state
        console.log('Attempting to recover from invalid signaling state...');
        setTimeout(() => {
          restartFullConnection();
        }, 1000);
        return;
      }
  
      const sender = peerConnection
        .getSenders()
        .find((s) => s.track?.kind === 'video');
      if (sender) {
        const params = sender.getParameters();
        params.degradationPreference = 'maintain-resolution';
        
        if (!params.encodings) {
          params.encodings = [
            { 
              rid: 'high', 
              active: true, 
              scaleResolutionDownBy: 1.0, 
              maxBitrate: 24000000
            },
            { 
              rid: 'medium', 
              active: true, 
              scaleResolutionDownBy: 1.0, 
              maxBitrate: 16000000
            }
          ]
        } else {
          params.encodings.forEach(encoding => {
            encoding.scaleResolutionDownBy = 1.0;
            encoding.maxBitrate = 24000000;
            (encoding as any).priority = 'high';
          });
        }
        
        try {
          await sender.setParameters(params);
          setSignalStatus('success');
          setShowMessage('');
        } catch (paramError) {
          console.warn('Failed to set sender parameters:', paramError);
          // Continue anyway as this is not critical
          setSignalStatus('success');
          setShowMessage('');
        }
      } else {
        const errorMessage = 'Signal failed - No video sender found';
        console.error('No video sender found.');
        setSignalStatus('failed');
        setShowMessage(errorMessage);
      }
    } catch (error) {
      let errorMessage = 'Signal failed - Network error';
      
      if (error instanceof Error) {
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          errorMessage = 'Signal failed - Network connection error';
        } else if (error.name === 'AbortError') {
          errorMessage = 'Signal failed - Request timeout';
        } else if (error.name === 'NetworkError') {
          errorMessage = 'Signal failed - Network unavailable';
        } else {
          errorMessage = `Signal failed - ${error.message}`;
        }
      }
      
      console.error('Error sending Bifrost signal:', error);
      setSignalStatus('failed');
      setShowMessage(errorMessage);
      
      // Attempt recovery based on error type
      if (error instanceof Error) {
        if (error.name === 'TypeError' || error.name === 'NetworkError') {
          // Network issues - retry after delay
          setTimeout(() => {
            if (peerConnectionRef.current?.localDescription) {
              console.log('Retrying signal after network error...');
              sendBifrostSignal(peerConnectionRef.current, peerConnectionRef.current.localDescription);
            }
          }, 3000);
        } else if (error.name === 'AbortError') {
          // Timeout - try with longer timeout or restart connection
          setTimeout(() => {
            attemptConnectionRecovery();
          }, 1000);
        }
      }
    }
  };
  
  const sendIceCandidateToServer = async (candidate: RTCIceCandidate) => {
    try {
      console.log('Sending ICE candidate to server:', candidate);

      const response = await fetch(
        'https://d.api.enncrypto.com/icecandidate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionDescription: '',
            sessionId: '12123213123123',
            context: '12312312123',
          }),
        }
      );

      const result = await response.json();
      console.log('ICE candidate sent successfully:', result);
    } catch (error) {
      console.error('Error sending ICE candidate:', error);
    }
  };

  const stopRTCConnection = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    if (bandwidthCheckIntervalRef.current) {
      clearInterval(bandwidthCheckIntervalRef.current);
      bandwidthCheckIntervalRef.current = null;
    }

    // Clear retry timeout when stopping
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // Reset retry count and statuses
    setRetryCount(0);
    setWebrtcStatus('pending');
    setSignalStatus('pending');
    
    // Clear image count timeout and reset state
    if (imageCountTimeoutRef.current) {
      clearTimeout(imageCountTimeoutRef.current);
      imageCountTimeoutRef.current = null;
    }
  };

  const setMaxBitrate = (sdp: any, maxBitrate: number): string => {
    console.log('setMaxBitrate');
    const lines = sdp.split('\n');
    let isVideoSection = false;
    let maxBitrateLineIndex = -1;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('m=video')) {
        isVideoSection = true;
      }
      if (isVideoSection && lines[i].startsWith('b=AS')) {
        maxBitrateLineIndex = i;
        break;
      }
    }

    if (maxBitrateLineIndex !== -1) {
      const maxBitrateLine = `b=AS:${maxBitrate}`;
      lines[maxBitrateLineIndex] = maxBitrateLine;
    } else if (isVideoSection) {
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].startsWith('m=video')) {
          lines.splice(i + 1, 0, `b=AS:${maxBitrate}`);
          break;
        }
      }
    }

    return lines.join('\n');
  };

  const startVideoStream = async () => {
    try {
      setShowMessage('Connecting');
      
      // Reset all statuses when starting
      setMqttStatus('pending');
      setWebrtcStatus('pending');
      setSignalStatus('pending');
      
      // Reset image count and related states
      setImageCount(0);
      if (imageCountTimeoutRef.current) {
        clearTimeout(imageCountTimeoutRef.current);
        imageCountTimeoutRef.current = null;
      }
      
      // Start MQTT connection first
      connectMQTT();
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      let selectedDeviceId = null;
  
      for (const device of videoDevices) {
        if (
          device.label.toLowerCase().includes('back') &&
          !device.label.toLowerCase().includes('wide') &&
          !device.label.toLowerCase().includes('ultra')
        ) {
          selectedDeviceId = device.deviceId;
          break;
        }
      }
  
      if (!selectedDeviceId && videoDevices.length > 0) {
        selectedDeviceId = videoDevices[0].deviceId;
      }
  
      const videoConstraints: VideoTrackConstraints = {
        deviceId: selectedDeviceId ? { ideal: selectedDeviceId } : undefined,
        facingMode: selectedDeviceId ? undefined : { ideal: 'environment' },
        width: { min: 2160, ideal: 2160, max: 2160 },
        height: { min: 1440, ideal: 1440, max: 1440 },
        frameRate: { min: 6, ideal: 6, max: 6 },
        advanced: []
      };
  
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false
      });
  
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
  
      streamRef.current = stream;
  
      // Reset retry count when starting a new stream
      setRetryCount(0);
      
      startRTCConnection(stream);
      setIsPaused(false);
      setIsStreaming(true);
      if (clientRef.current) {
        clientRef.current.publish(MQTT_TOPIC, 'Stream started');
      }
      videoRef.current?.play();
      setCornerOverlayVisible(true);
    } catch (error) {
      console.error('Error accessing video stream:', error);
      setShowMessage('Failed to access camera');
    }
  };

  const stopVideoStream = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      streamRef.current = null;
    }
    stopRTCConnection();
    setIsStreaming(false);
    setIsPaused(false);
    if (clientRef.current) {
      clientRef.current.publish(MQTT_TOPIC, 'Stream stopped');
    }
    disconnectMQTT();
    removeCornerOverlay();
  };

  const toggleStream = () => {
    if (isStreaming) {
      setSessionEnd(true);
      stopVideoStream();
      stopTimer();
    } else {
      startVideoStream();
    }
  };

  const togglePause = () => {
    if (isPaused) {
      videoRef.current?.play();
      const mediaStream = videoRef.current?.srcObject as MediaStream;
      mediaStream?.getVideoTracks().forEach((track) => {
        track.enabled = true;
      });
      startTimer();
    } else {
      videoRef.current?.pause();
      const mediaStream = videoRef.current?.srcObject as MediaStream;
      mediaStream?.getVideoTracks().forEach((track) => {
        track.enabled = false;
      });
      stopTimer();
    }
    setIsPaused(!isPaused);
  };

  // Progress indicator component
  const ProgressIndicator = ({ status, label }: { status: 'pending' | 'success' | 'failed', label: string }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'success':
          return '#22c55e'; // green
        case 'failed':
          return '#ef4444'; // red
        case 'pending':
        default:
          return '#6b7280'; // gray
      }
    };

    return (
      <div
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '3px',
          border: `2px solid ${getStatusColor()}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '10px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: 'transparent',
          marginRight: '4px'
        }}
      >
        {label.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <>
      {!showSessionEnd ? (
        <>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <div className="relative w-full h-full bg-black">
            {showMessage && (
              <div
                className="absolute w-full py-2 text-center"
                style={{
                  backgroundColor: 'black',
                  color: 'white',
                  textAlign: 'center',
                  position: 'absolute',
                  top: '40px',
                  width: '90%',
                  fontSize: '12px',
                  marginLeft: '5%',
                }}
              >
                {showMessage}
              </div>
            )}

            <video
              ref={videoRef}
              className="fixed top-0 left-0 w-full h-full object-cover z-0"
              autoPlay
              muted
              playsInline
              disablePictureInPicture
              style={{ width: '100vw', height: '100vh' }}
            ></video>

            {createCornerOverlay()}

            <div
              className="absolute bottom-10 w-full flex justify-center space-x-4 z-10"
              style={{
                display: 'flex',
                justifyContent: 'center',
                position: 'absolute',
                bottom: 0,
                width: '100%',
              }}
            >
              <button
                onClick={toggleStream}
                className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg"
                style={{
                  background: 'red',
                  borderRadius: '50%',
                  border: '0px',
                  width: '50px',
                  height: '50px',
                  position: 'relative',
                  alignItems: 'center',
                  textAlign: 'center',
                  zIndex: 10,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '14px',
                    color: 'white',
                  }}
                >
                  {isStreaming ? 'Stop' : 'Start'}
                </span>
              </button>

              {/* Timer */}
              <div
                className="absolute bottom-10 w-full text-white text-lg"
                style={{
                  textAlign: 'center',
                  zIndex: 10,
                  position: 'absolute',
                  left: '22px',
                  top: '13px',
                  background: 'black',
                  padding: '3px 7px',
                  borderRadius: '4px',
                }}
              >
                {formatTime(time)}
              </div>

              {/* Progress Indicators - Bottom Right */}
              <div
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '8px',
                  borderRadius: '5px',
                  zIndex: 20,
                }}
              >
                <ProgressIndicator status={mqttStatus} label="MQTT" />
                <ProgressIndicator status={webrtcStatus} label="RTC" />
                <ProgressIndicator status={signalStatus} label="SIGNAL" />
              </div>
            </div>

            <div
              className="absolute top-4 right-4 flex items-center space-x-2 z-20"
              style={{
                position: 'absolute',
                top: '55px',
                right: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '8px',
                borderRadius: '5px',
                padding: '5px 10px',
                background: 'black',
              }}
            >
              <div
                className="w-8 h-8 flex items-center justify-center bg-blue-500 rounded-full text-white"
                style={{
                  fontSize: '18px',
                  marginRight: '8px',
                }}
              >
               <div>Image Count: </div>
              </div>
              <div className="text-white text-xl">{imageCount}</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100vh',
              background: 'white',
              color: 'black',
              fontWeight: 500,
              fontSize: '21px',
              flexDirection: 'column',
              textAlign: 'center',
            }}
          >
            <h2 style={{ fontSize: '24px', fontWeight: 600 }}>
              Task Completed
            </h2>

            <p
              style={{
                fontSize: '18px',
                marginBottom: '30px',
                width: '85%',
                marginTop: 15,
                whiteSpace: 'pre-line',
              }}
            >
              {`Task has been completed successfully!`}
            </p>
            <div
              style={{
                border: '1px solid #4000aa',
                padding: '15px 68px',
                marginTop: '50px',
                borderRadius: '8px',
                background: 'white',
                textAlign: 'center',
                fontSize: '17px',
                color: '#4000aa',
                cursor: 'pointer',
              }}
              onClick={() => {
                window.location = window.location;
              }}
            >
              Go to Homepage
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default VideoStream;